import Sidebar from "@/customComponents/dashBoardComponents/Sidebar.jsx";
import {hasFlightLanded} from '@/features/fromAirportArrival/useFlightArrivalTrigger'
import {hasArrivalGuide, isFromAirportTrip} from '@/features/fromAirportArrival/arrivalEligibility'
import {useArrivalGuideWindow} from '@/features/fromAirportArrival/useArrivalGuideWindow'
import {useMyLocation} from '@/hooks/useMyLocation'
import {useShareMyLocation} from '@/hooks/useShareMyLocation'
import {useOngoingTrips, useTripDetails, useTripsDetails} from '@/hooks/useTrips'
import Header from "@/customComponents/dashBoardComponents/Header.jsx";
import { Outlet, useLocation } from "react-router";
import { useSidebar } from "@/context/SidebarContext.jsx";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import useMinimalLayout from "@/hooks/useMinimalLayout.js";
import WelcomeModal from '@/customComponents/fromAirportArrival/WelcomeModal.jsx'
import ArrivalGuideButton from '@/customComponents/fromAirportArrival/ArrivalGuideButton.jsx'
import ResumeRideModal from '@/customComponents/sharedComponents/ResumeRideModal.jsx'
import PickupUpdatedModal from '@/customComponents/fromAirportArrival/PickupUpdatedModal.jsx'
import ReachedPickupModal from '@/customComponents/fromAirportArrival/ReachedPickupModal.jsx'
import {usePickupGeofenceTarget, useReachedPickupGeofence} from '@/hooks/useReachedPickupGeofence'
import NotificationToasts from '@/customComponents/sharedComponents/NotificationToasts.jsx'

/**
 * ⚠️ THE ROUTE-SCOPING HELPERS THAT STOOD HERE ARE GONE (2026-09-22).
 *
 * `ARRIVAL_AWARE_PATTERNS`, `RIDE_LIST_PATHS`, `isArrivalAwareRoute` and
 * `isTripDetailsRoute` existed to answer one question: on which pages may a landed flight
 * REDIRECT the passenger, and on which must it merely ask? That question mattered because
 * the arrival guide was a page, so showing it meant taking the passenger off whatever they
 * were reading — and the answer had to be careful, hence a matcher that also knew
 * `/rides/ongoing` is a listing rather than a ride called "ongoing".
 *
 * The guide is a modal now. It opens over the page the passenger chose and closes back to
 * it, so there is no page to lose and nothing to be careful about: it shows everywhere,
 * which is what was asked for. The whole apparatus deleted itself.
 */

export default function AppLayout() {
    const { isMobileOpen, toggleMobileSidebar, isCollapsed } = useSidebar()
    const location = useLocation()
    // Staff-handoff deep link (`?layout=minimal`, on ANY route): no sidebar, no header,
    // no sidebar margin — the passenger came from an SMS to confirm one booking, not to
    // browse the dashboard. See useMinimalLayout.
    const minimal = useMinimalLayout()

    /*
     * ── THE FLIGHT-LANDED TRIGGER ──────────────────────────────
     *
     * ⚠️ UNSCOPED, ON EVERY PAGE. It used to be limited to the three screens that are about
     * one ride, because firing meant NAVIGATING and a redirect hijacks a page chosen for
     * another reason. It opens a modal now, which hijacks nothing — so the passenger is told
     * their flight is down wherever they happen to be.
     *
     * It lives in the LAYOUT because the watch has to survive every navigation: armed per
     * screen, each one would re-arm it from scratch and the poll that notices a delayed
     * landing would restart with it.
     *
     * ⚠️ IT READS THE TRIP, NOT A STOPWATCH. The mobile apps count down to a timestamp
     * written at booking; the backend now tracks the flight itself and puts the answer on
     * the trip, so this watches that instead. See `useFlightArrivalTrigger`.
     *
     * ⚠️ AND IT NEEDS THE FULL TRIP. The ongoing LIST names the live ride; the flight
     * record hangs off the trip resource. One shared query key, so the tracking page's own
     * fetch and this one are the same request. The slow poll is what notices the backend
     * moving `expected_landing_at` after a delay — without it the timer would be armed
     * against whatever number happened to be there when the tab opened.
     */
    /* ⚠️ THE WHOLE ONGOING LIST, not one row: the passenger's position is sent to EVERY
       ongoing trip (see the share below). */
    const {data: ongoing} = useOngoingTrips({page: 1, perPage: 20})
    const ongoingTrips = ongoing?.trips || []

    /**
     * ⚠️ THE FROM-AIRPORT RIDE THE GUIDE IS ABOUT — the FIRST one on the ongoing list, not
     * simply the first ongoing trip (2026-09-23).
     *
     * It used to be `ongoingTrips[0]`, whatever that was. A passenger with a city ride
     * ongoing and an airport pickup behind it on the list therefore got no plane button and
     * no arrival modal at all: the flow looked at the wrong ride and found nothing airport
     * about it.
     *
     * ⚠️ FILTERED OFF THE LIST ROW, WHICH CAN ANSWER THIS. `TripResource` carries
     * `trip_direction` on a listing; what it does NOT carry is `flight`, which is why the
     * full trip is still fetched below for anything about the aircraft.
     *
     * ⚠️ AND THE FIRST IS THE ONE, DELIBERATELY. Two from-airport rides ongoing at once is
     * not a case the guide tries to disambiguate — there is one button and it opens the
     * first, as asked.
     */
    const guideTripId = ongoingTrips.find(isFromAirportTrip)?.id ?? null

    /* ⚠️ THE FULL TRIP, because everything about the FLIGHT hangs off the trip resource and
       none of it is on the list row. 2s is the portal's live rate; the query key is the one
       /track-driver already polls, so on that page it is the same request, not a second. */
    const {data: guideTripData} = useTripDetails(guideTripId, {enabled: !!guideTripId, pollMs: 2000})
    const guideTrip = guideTripData?.trip || null

    /**
     * ── SHARE THE PASSENGER'S LOCATION, FROM ANY PAGE ────────────────────────────
     *
     * ⚠️ WHY APP-WIDE. This used to live on /welcome and /track-driver only, so a passenger
     * who opened their ride list, or settings, or a receipt, went dark on the driver's map
     * for as long as they were there. The driver's pin, the walk ETA and the "has arrived"
     * detection all run on this one stream; there is no endpoint that returns a
     * passenger's last position to fill the gap.
     *
     * ⚠️ LANDED, NOT "NOT IN THE AIR". `hasFlightLanded` is true only on the backend's own
     * `flight_status` reading Arrived/Landed. A flight with no record yet, or whose tracking
     * failed, is NOT landed here — sharing on a missing status would ask for a location
     * mid-flight, and a browser gives one clean prompt per origin.
     *
     * ⚠️ THE PROMPT IS GATED BY THE SAME RULE. `useMyLocation` does not start its watch —
     * and so the browser does not ask — until a trip passes it.
     */
    /* ⚠️ THE RULE, AND ALL OF IT: the trip is `EnRoute` — the driver is on the way.
       Not before (nobody coming yet), not `Arrived`/`InProgress` (the driver is at the
       kerb or the passenger is in the car). A from-airport trip additionally needs the
       flight LANDED: until then the passenger is in the air.

       The rule TURNS SHARING ON; once any ongoing trip passes it, the position goes to
       EVERY ongoing trip (product decision — each driver on this passenger's trips sees
       the pin). Each EnRoute trip is judged from its FULL DETAIL: the ongoing list is not
       guaranteed to carry `flight.flight_status`. */
    const shouldShareFor = (trip) => !!trip && trip.status === 'EnRoute'
        && (!isFromAirportTrip(trip) || hasFlightLanded(trip))
    const enRouteIds = ongoingTrips.filter((t) => t?.status === 'EnRoute').map((t) => t.id)
    const enRouteTrips = useTripsDetails(enRouteIds, {pollMs: 5000})
    const shareTripIds = enRouteTrips.filter(shouldShareFor).map((t) => t.id)
    const ongoingTripIds = ongoingTrips.map((t) => t?.id).filter((id) => id != null)

    /* WHY EACH ONGOING TRIP IS OR IS NOT SHARED — always printed, but on change only.
       Every failed gate looks the same from the driver's side (no pin), and this line is
       what says which one it was. */
    const shareVerdict = ongoingTrips.map((t) => {
        const full = enRouteTrips.find((d) => d && String(d.id) === String(t.id)) || t
        const reason = t?.status !== 'EnRoute' ? `status is ${JSON.stringify(t?.status)}, not EnRoute (does not turn sharing on)`
            : !enRouteTrips.some((d) => d && String(d.id) === String(t.id)) ? 'detail not loaded yet'
            : isFromAirportTrip(full) && !hasFlightLanded(full)
                ? `from_airport, flight_status is ${JSON.stringify(full?.flight?.flight_status ?? null)}`
                : 'PASSES — turns sharing on'
        return `trip ${t?.id} (${full?.trip_direction ?? '?'}): ${reason}`
    }).join(' | ') + (shareTripIds.length > 0 ? ` → SENDING TO ALL: ${ongoingTripIds.join(', ')}` : ' → NOT SHARING')
    useEffect(() => {
        console.log('%c[share]', 'color:#208AEF;font-weight:600', shareVerdict || 'no ongoing trips')
    }, [shareVerdict])
    const sharePassengerLocation = shareTripIds.length > 0
    /* ⚠️ ONE WATCH, TWO REASONS. The pickup geofence needs the position from the moment the trip is
       Assigned/Accepted/EnRoute — earlier than sharing starts — so the watch is on for either.
       SHARING keeps its own, narrower gate below: widening this must not start telling a driver
       where the passenger is any sooner. */
    /* The geofence watches ANY ongoing trip its direction is switched on for (pickup_arrival
       config), not just the from-airport guide trip. Same detail queries as `enRouteTrips`. */
    const pickupTarget = usePickupGeofenceTarget(enRouteTrips)
    const passengerFix = useMyLocation({enabled: sharePassengerLocation || pickupTarget.wanted})
    const reachedPickupTripId = useReachedPickupGeofence(pickupTarget.trip, pickupTarget, passengerFix.position)
    useShareMyLocation(ongoingTripIds, passengerFix.position, {enabled: sharePassengerLocation})

    /* ⚠️ THE GUIDE GATE IS CHECKED AT FIRE TIME, not baked into the watch. Landing is
       landing whatever the airport; whether we have a WALK for it is a separate question,
       and a from-airport ride into an airport we have no guide for should simply not
       navigate rather than be walked through Pearson. */
    /* ⚠️ THE ROUTE GATE IS A NULL TRIP, not a branch inside the callback. The watch reads
       its trip id to decide whether to arm at all, so handing it nothing off-route leaves
       it disarmed rather than armed-and-suppressed — no timer waiting to fire the instant
       the passenger happens to open something unrelated. */
    /**
     * ⚠️ OPEN OR CLOSED, NOT WHICH TRIP (2026-09-23). This used to hold the landed trip
     * itself, which made two answers to "which ride is the guide about" — the held one and
     * `guideTrip` above — that could disagree the moment the ongoing list moved.
     *
     * `guideTrip` is the single answer now, so all that is left to remember is whether the
     * passenger has the guide open. It also means the modal closes itself for free when the
     * ride leaves the ongoing list: no trip, nothing to show.
     */
    const [guideOpen, setGuideOpen] = useState(false)

    /**
     * ⚠️ THE BUTTON'S CONDITION: a from-airport ride on the ongoing list, inside its window
     * — from two hours before the pickup until the ride starts. `useArrivalGuideWindow` owns
     * the two ends and the tick that opens the first one; see there for why it ticks at all.
     *
     * It is NOT gated on the flight being down, deliberately. "Where do I go when I land" is
     * a question asked before the aircraft arrives, and the guide answers it then too — it
     * renders `FlightOnTheWay` while the flight is up.
     *
     * `guideTrip` is already from-airport by construction; `hasArrivalGuide` is the
     * eligibility question on top of it, and stays the one place that answers it.
     */
    const guideWindowOpen = useArrivalGuideWindow(guideTrip)
    const guideAvailable = !!guideTrip && hasArrivalGuide(guideTrip) && guideWindowOpen

    /* ⚠️ DERIVED, NOT SYNCHRONISED — no effect clears this when the ride moves on. */
    const welcomeTrip = guideOpen && guideAvailable ? guideTrip : null

    /**
     * ⚠️ THE GUIDE NO LONGER OPENS ITSELF (2026-09-23) — the plane button is the way in.
     *
     * `useFlightArrivalTrigger` stood here and called `setGuideOpen(true)` when the flight
     * reported down. Its once-per-trip guard is a REF, deliberately not persisted, so every
     * reload counted as the first time: a passenger who had already read the guide, closed
     * it, and refreshed for any reason got "Welcome! You've arrived" over their page again,
     * and again after that.
     *
     * Nothing is lost by dropping it. The plane button stands for the whole of the window
     * the modal used to open in (see `guideAvailable`), so the guide is one tap away rather
     * than in the way — and the ride a passenger reloads into is now announced by
     * `ResumeRideModal`, which asks rather than appears.
     *
     * `hasFlightLanded` is still imported from that module: the location sharing above
     * reads it. Only the trigger is gone.
     */

    /**
     * ── BUT A LANDING SEEN LIVE DOES OPEN IT ─────────────────────────────────────
     * When the flight goes from NOT landed to landed while this page is open (the 2s trip
     * poll above picks it up), the guide opens by itself — under the same conditions that
     * show the plane button (`guideAvailable`).
     *
     * ⚠️ ONLY ON THE CHANGE, never on a first read. A trip that is ALREADY landed when the
     * page loads is the reload case the note above is about, and stays button-only.
     */
    const guideLanded = hasFlightLanded(guideTrip)
    const lastLandedRef = useRef({tripId: null, landed: null})
    useEffect(() => {
        const tripId = guideTrip?.id ?? null
        const last = lastLandedRef.current
        if (tripId != null && String(last.tripId) === String(tripId) && last.landed === false
            && guideLanded && guideAvailable) {
            setGuideOpen(true)
        }
        lastLandedRef.current = {tripId, landed: tripId != null ? guideLanded : null}
    }, [guideTrip?.id, guideLanded, guideAvailable])


    useEffect(() => {
        if (isMobileOpen) {
            const prev = document.body.style.overflow
            document.body.style.overflow = 'hidden'
            return () => { document.body.style.overflow = prev }
        }
    }, [isMobileOpen])

    return (
        <div className="min-h-screen">
            {/* ⚠️ AT THE LAYOUT'S ROOT, ALONGSIDE THE WATCH THAT RAISES IT. Rendered inside
                the routed content it would unmount the moment the passenger navigated, and
                an unanswered question would disappear on a click that had nothing to do
                with it. `Modal` portals itself, so the position here is about lifetime
                rather than layout. */}
            {/* A flight-shifted pickup, on whatever page the passenger is on. It also owns the
                user-channel subscription, so it has to live for the whole signed-in session. */}
            <PickupUpdatedModal/>

            {/* Every other runtime notification, as a toast — listens on the channel above.
                Not in minimal layout: a passenger confirming one booking from the desk's link
                has no use for toasts sliding in over it. */}
            {!minimal && <NotificationToasts/>}

            {/* ⚠️ "YOU HAVE A RIDE IN PROGRESS", ONCE PER PAGE LOAD — and NOT while the
                arrival guide is open. Both are dialogs that raise themselves, and two of
                them stacked is a passenger dismissing a question to find another question.
                The guide wins: it is about a flight that has just landed, which is the more
                urgent of the two, and this one is still there once it is closed.

                ⚠️ AND NOT IN MINIMAL LAYOUT. `?layout=minimal` is the phone desk's hand-off
                — someone confirming ONE booking from an SMS, often with no session they know
                about. A dialog about another ride is not what that page is for. */}
            {/* ⚠️ NO LONGER GATED ON THE GUIDE. It was suppressed while the arrival modal
                was open, because that one raised ITSELF on a landing and two self-opening
                dialogs stack. The guide only opens on a tap now, so this is the one thing a
                reload shows — which is what it is for. */}
            {!minimal && <ReachedPickupModal tripId={reachedPickupTripId}/>}
            {!minimal && <ResumeRideModal trip={ongoingTrips[0] || null}/>}

            {/* The arrival guide itself, over whatever page is open. See `WelcomeModal`
                for why it stopped being a page. */}
            <WelcomeModal trip={welcomeTrip} onClose={() => setGuideOpen(false)}/>

            {/* ⚠️ THE ONLY WAY IN, now that `/:tripId/welcome` is gone. The guide is the whole
                arrival experience — the booth, the walk, the driver — and a modal the
                passenger closes is a modal they would otherwise lose until a reload. So the
                plane stands for as long as there is a from-airport ride on the ongoing list. */}
            {guideAvailable && (
                <ArrivalGuideButton onClick={() => setGuideOpen(true)}/>
            )}

            {!minimal && <Sidebar />}

            <AnimatePresence>
                {!minimal && isMobileOpen && (
                    <motion.div
                        key="sidebar-backdrop"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.25, ease: 'easeOut'}}
                        className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-[2px] xl:hidden"
                        onClick={toggleMobileSidebar}
                    />
                )}
            </AnimatePresence>

            <div className={`flex-1 flex flex-col min-h-screen transition-[margin] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${minimal ? '' : (isCollapsed ? 'xl:ml-20' : 'xl:ml-72.5')}`}>
                {!minimal && (
                    <header className="sticky top-0 w-full bg-white z-30 shadow-[0px_8px_20px_0px_rgba(0,0,0,0.16)]">
                        <Header />
                    </header>
                )}
                <div className={`flex-1 ${location.pathname.startsWith('/book-ride/destination') ? 'p-4 bg-(--bg-grey)' : 'py-5 px-3 md:p-10'}`}>
                    <motion.div
                        key={location.pathname}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{duration: 0.2, ease: 'easeOut'}}
                    >
                        <Outlet />
                    </motion.div>
                </div>
            </div>
        </div>
    )
}
